import Vue, { VueConstructor } from 'vue';
import Router, { RouteConfig } from 'vue-router';
import EmptyLayout from '@/layouts/EmptyLayout.vue';
import Cars from './modules/Cars';
import UI from './modules/UI';
import Dashboard from '@/pages/Dashboard/dashboard.vue';
import Login from '@/pages/login.vue';
import Register from '@/pages/register.vue';
import ConfirmRegistration from '@/pages/confirm-registration.vue';
import ConfirmPasswordReset from '@/pages/confirm-password-reset.vue';
import BigLoading from '@/pages/big-loading.vue';
import NotFound from '@/pages/not-found.vue';
import Forbidden from '@/pages/forbidden.vue';
import CompanySettings from './modules/CompanySettings';
import Customers from './modules/Customers';
import Departments from './modules/Departments';
import Projects from './modules/Projects';
import Products from './modules/Products';
import Orders from './modules/Orders';
import Invoices from './modules/Invoices';
import Employees from './modules/Employees';
import TabTitle from '@/utils/tabtitle';
import TabHistory, { IRouteWithoutCircularReference } from '@/utils/tab-history';
import RecurringOrders from './modules/RecurringOrders';
import { ApplicationMode } from '@/utils/application-mode-service';
import SubUnits from './modules/SubUnits';
import Occupations from './modules/Occupations';
import Municipalities from './modules/Municipalities';
import ApplicationSettings from './modules/ApplicationSettings';
import UnitMainPaycheckLineTemplates from './modules/UnitMainPaycheckLineTemplates';
import PaycheckLineTemplate from './modules/PaycheckLineTemplate';
import Paychecks from './modules/Paychecks';
import PaycheckPeriods from './modules/PaycheckPeriods';
import Ameldings from './modules/Ameldings';
import PaycheckReports from './modules/PaycheckReports';
import ProductGroups from './modules/ProductGroups';
import PaymentMethods from './modules/PaymentMethods';
import PensionUnitSelections from './modules/PensionUnitSelections';
import AdvancedPaychecks from './modules/AdvancedPaychecks';
import WebstoreApiTokens from './modules/WebstoreApiTokens';
import Warehouse from './modules/Warehouse';
import Timesheets from './modules/Timesheets';
import SomethingWentWrong from '../pages/something-went-wrong.vue';

export type ISnotrRouteWithoutChildren = RouteConfig & {
  name: string;
  meta?: {
    title?: string;
    applicationMode?: ApplicationMode;
    layout?: VueConstructor<Vue>;
  };
  children?: never;
}

export type ISnotrRouteWithChildren = RouteConfig & {
  name?: never;
  meta?: {
    title?: string;
    applicationMode?: ApplicationMode;
    layout?: VueConstructor<Vue>;
  };
  children: ISnotrRoute[];
}

export type ISnotrRoute = ISnotrRouteWithChildren | ISnotrRouteWithoutChildren;

Vue.use(Router);

const baseRoutes: ISnotrRoute[] = [
  {
    path: '/',
    name: 'Dashboard',
    component: Dashboard,
    meta: {
      title: 'TabTitle.dashboard',
    },
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { layout: EmptyLayout },
  },
  {
    path: '/register',
    name: 'Register',
    component: Register,
    meta: { layout: EmptyLayout },
  },
  {
    path: '/confirm-registration',
    name: 'ConfirmRegistration',
    component: ConfirmRegistration,
    meta: { layout: EmptyLayout },
  },
  {
    path: '/confirm-password-reset',
    name: 'ConfirmPasswordReset',
    component: ConfirmPasswordReset,
    meta: { layout: EmptyLayout },
  },
  {
    path: '/big-loading',
    name: 'BigLoading',
    component: BigLoading,
    meta: {
      title: 'loading',
    },
  },
];

const serviceRoutes: ISnotrRoute[] = [
  {
    path: '/forbidden',
    name: 'Forbidden',
    component: Forbidden,
  },
  {
    path: '/not-found',
    name: 'NotFound',
    component: NotFound,
    meta: {
      layout: EmptyLayout,
      title: 'TabTitle.notFound',
    },
  },
  {
    path: '/something-went-wrong',
    name: 'SomethingWentWrong',
    component: SomethingWentWrong,
    meta: {
      layout: EmptyLayout,
      title: 'TabTitle.somethingWentWrong',
    },
  },
  {
    path: '*',
    name: 'NotFoundMatch',
    component: NotFound,
    meta: {
      layout: EmptyLayout,
      title: 'TabTitle.notFound',
    },
  },
];

let routes: ISnotrRoute[] = baseRoutes.concat(
  Cars,
  UI,
  CompanySettings,
  Customers,
  Departments,
  Projects,
  Products,
  Orders,
  Invoices,
  RecurringOrders,
  SubUnits,
  Occupations,
  Municipalities,
  Employees,
  ApplicationSettings,
  UnitMainPaycheckLineTemplates,
  PaycheckLineTemplate,
  Paychecks,
  PaycheckPeriods,
  Ameldings,
  ProductGroups,
  PaymentMethods,
  PensionUnitSelections,
  PaycheckReports,
  AdvancedPaychecks,
  WebstoreApiTokens,
  Warehouse,
  Timesheets,
);

routes = [
  ...routes,
  ...serviceRoutes,
];

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior: () => ({ x: 0, y: 0 }),
});

router.beforeEach((to, from, next) => {
  const nearestTitle = to.matched.slice().reverse().find(r => r.meta && r.meta.title);
  if (nearestTitle) {
    const fields: object = nearestTitle.meta.tFields || {};
    TabTitle.set(nearestTitle.meta.title, fields);
  } else {
    TabTitle.appTitle();
  }

  if (from.name === null) { // this happens when the user refreshes or navigates from the outside
    TabHistory.restoreHistory();
  }

  // we only need meta from matched, there is a circular reference error if whole object is passed :(
  TabHistory.add({ ...to, matched: to.matched.map(match => { return { meta: match.meta }})} as IRouteWithoutCircularReference);

  next();
});

router.afterEach((to, from) => {
  if (
    from.name && to.name &&
    from.name === to.name &&
    (
      from.query?.filters ||
      to.query?.filters
    )
  ) {
    router.app.$root.$emit('resetTableFilters');
  }
});

export default router;
